import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import _get from 'lodash/get';
import TaskCompletionForm from 'components/TaskCompletionForm/TaskCompletionForm';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { useTranslation } from 'react-i18next';
import { APP_URLS } from 'constants/urls';
import { useNavigate } from 'react-router-dom';
import FormTemplateFieldNames from '@lib/enums/fieldNames/formTemplateFieldNames';
import { ActionsProps } from '@lib/components/ReactHookForm/types';
import toast from '@lib/components/Toast/Toast';
import { showGraphQLErrors } from '@lib/utils/graphQLErrors';
import {
  Documentation,
  DocumentationValidationStatusEnum,
  FormTemplateQuestion,
  FormTemplateQuestionFieldsKindEnum,
} from 'graphql-common';
import {
  useEdmDocumentationVersionReadCreateMutation,
  useEdmDocumentationVersionReadIncrementMutation,
} from '@lib/graphql-common';
import { DocumentReadRecord } from 'components/TaskCompletionForm/DocumentQuestionField';
import useFields from './utils/useFields';
import useActions from './utils/useActions';
import isDocOptionSatisfied from './utils/isDocOptionSatisfied';

/**
 * Task completion form for the `/perform-tasks/:id/completion-add` route.
 *
 * Owns the session-level state that has to survive across individual DOCUMENT
 * question fields:
 * - `readRecords` — `{ docId → { versionReadId, versionId } }` populated by
 *   `onMarkAsRead` after a successful `documentationVersionReadCreate`, used
 *   to flip "Unread" chips to "Read" and to attach `documentationVersionReadId`
 *   to the submit resp for `status: READ`. Re-opens of an already-read doc
 *   with the same versionId fire `documentationVersionReadIncrement` against
 *   the saved id instead — BE accumulates reading time into one record per
 *   doc per form session.
 * - `docsByFieldId` — `{ fieldId → { docId → Documentation } }`, aggregated
 *   via `registerFieldDocs` as each `DocumentQuestionField` resolves its own
 *   ≤5-id documentations query. Needed at submit time so `getAnswers`
 *   (`processDocument`) can decide `READ / UNAVAILABLE(NotValidated/Deleted/
 *   Offline) / UNREAD` per option using the correct per-field document set.
 *
 * `onSuccess` threading: `onMarkAsRead` only closes the FileViewerModal after
 * the mutation resolves (BE errors keep the modal open so the user doesn't
 * lose doc context). Applies to both create and increment branches.
 */
function TaskCompletionCreateContainer() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    formRef,
    id,
    isFormAvailable,
    isFormDisabled,
    isViewLocked,
    onTaskCompletionCreateMutation,
    taskCompletionError,
    taskCompletionCreateMutationLoading,
    taskCompletionLoading,
    taskDataForFormTemplate,
    taskFormTemplateError,
    taskFormTemplateLoading = false,
    taskFormTemplateVersion,
  } = useActions();

  const questions = _get(
    taskFormTemplateVersion,
    FormTemplateFieldNames.Questions,
    [],
  ) as FormTemplateQuestion[];
  const lastAllowedAnswers = _get(
    taskDataForFormTemplate,
    FormTemplateFieldNames.LastAllowedAnswers,
    [],
  );

  const [readRecords, setReadRecords] = useState<
    Record<string, DocumentReadRecord>
  >({});
  // Mirror readRecords in a ref so onMarkAsRead can branch create-vs-increment
  // without taking readRecords as a dep (which would re-create the callback on
  // every read and cascade re-renders to every DocumentQuestionField).
  const readRecordsRef = useRef(readRecords);
  readRecordsRef.current = readRecords;
  const [docsByFieldId, setDocsByFieldId] = useState<
    Record<string, Record<string, Documentation>>
  >({});

  const registerFieldDocs = useCallback(
    (fieldId: string, docs: Record<string, Documentation>) => {
      setDocsByFieldId((prev) => ({ ...prev, [fieldId]: docs }));
    },
    [],
  );

  const [versionReadCreate, { loading: createLoading }] =
    useEdmDocumentationVersionReadCreateMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const [versionReadIncrement, { loading: incrementLoading }] =
    useEdmDocumentationVersionReadIncrementMutation({
      onError: (error) => showGraphQLErrors(error, t),
    });

  const markAsReadLoading = createLoading || incrementLoading;

  const onMarkAsRead = useCallback(
    (
      documentationId: string,
      versionId: string,
      readingDurationSeconds: number,
      isVideo: boolean,
      onSuccess?: () => void,
    ) => {
      const markedToastContent = t(
        isVideo ? 'marked-as-watched' : 'marked-as-read',
      );
      const existing = readRecordsRef.current[documentationId];
      const shouldIncrement = !!existing && existing.versionId === versionId;

      if (shouldIncrement) {
        versionReadIncrement({
          variables: {
            id: existing.versionReadId,
            readingDurationSeconds,
          },
          onCompleted: (data) => {
            if (!data?.documentationVersionReadIncrement?.id) return;
            toast({ content: markedToastContent });
            onSuccess?.();
          },
        });
        return;
      }

      versionReadCreate({
        variables: {
          documentationVersionId: versionId,
          readAt: new Date().toISOString(),
          readingDurationSeconds,
        },
        onCompleted: (data) => {
          const readId = data?.documentationVersionReadCreate?.id;
          if (!readId) return;
          setReadRecords((prev) => ({
            ...prev,
            [documentationId]: { versionReadId: readId, versionId },
          }));
          toast({ content: markedToastContent });
          onSuccess?.();
        },
      });
    },
    [versionReadCreate, versionReadIncrement, t],
  );

  const onLinkedDocsMarkedAsRead = useCallback(
    (documentationId: string, versionId: string, versionReadId: string) => {
      setReadRecords((prev) => ({
        ...prev,
        [documentationId]: { versionReadId, versionId },
      }));
    },
    [],
  );

  const { formFields, defaultValues, hasVisibleFields } = useFields(
    [...questions],
    lastAllowedAnswers,
    isFormDisabled,
    {
      readRecords,
      onMarkAsRead,
      registerFieldDocs,
      isMarkAsReadLoading: markAsReadLoading,
    },
    isViewLocked,
  );

  // For each DOCUMENT form-field, capture the parent fieldId and the list of
  // `documentationId`s flagged `requiredToRead`. The sync effect uses these
  // (together with `docsByFieldId`) to drive the per-field boolean form value
  // the Yup schema in `useFields` validates (`true` iff every required *and
  // available* doc is read).
  const requiredDocsByValueName = useMemo(() => {
    const result: Record<string, { fieldId: string; docIds: string[] }> = {};
    questions.forEach((question) => {
      question.fields?.forEach((field) => {
        if (field.kind !== FormTemplateQuestionFieldsKindEnum.Document) return;
        const docIds: string[] = [];
        (field.options || []).forEach((opt) => {
          const { optionDocumentation: od } = opt;
          if (od?.requiredToRead && od.documentationId) {
            docIds.push(od.documentationId);
          }
        });
        if (!docIds.length) return;
        result[`question-${question.id}-field-${field.id}`] = {
          fieldId: field.id,
          docIds,
        };
      });
    });
    return result;
  }, [questions]);

  useEffect(() => {
    if (isFormDisabled) return;
    const setValue = formRef.current?.setValue;
    if (!setValue) return;
    Object.entries(requiredDocsByValueName).forEach(
      ([valueName, { fieldId, docIds }]) => {
        const fieldDocs = docsByFieldId[fieldId];
        // Wait for the field's `useEdmDocumentationsLightQuery` to resolve before
        // computing read-state — otherwise unavailable docs would be wrongly
        // treated as required-and-unread on the first render.
        if (!fieldDocs) return;
        const availableRequired = docIds.filter(
          (docId) =>
            fieldDocs[docId]?.validationStatus ===
            DocumentationValidationStatusEnum.Validated,
        );
        // Counts both in-session marks-as-read and BE-acknowledged reads
        // (`Documentation.read === true`) so reload doesn't lose compliance.
        const allRead = availableRequired.every((docId) =>
          isDocOptionSatisfied(docId, fieldDocs[docId], readRecords),
        );
        setValue(valueName, allRead, { shouldValidate: true });
      },
    );
  }, [
    readRecords,
    requiredDocsByValueName,
    docsByFieldId,
    isFormDisabled,
    formRef,
  ]);

  const taskGroupKey = _get(taskDataForFormTemplate, 'groupKey') as
    | string
    | undefined;
  const formTemplateId = _get(taskDataForFormTemplate, 'formTemplate.id') as
    | string
    | undefined;

  useEffect(() => {
    if (!isFormAvailable && !taskFormTemplateLoading) {
      navigate(APP_URLS.app.tasks.view.getDynamicPath({ pathParts: { id } }));
    }
  }, [id, isFormAvailable, navigate, taskFormTemplateLoading]);

  const actionsComponent = ({ loading }: ActionsProps) =>
    isFormDisabled || !hasVisibleFields ? null : (
      <Button
        type="submit"
        buttonType={ButtonTypes.primaryFilled}
        buttonText={t('finish')}
        leftIcon="check"
        fullWidth
        disabled={loading}
        data-test-id="task-completion__submit-button"
      />
    );

  return (
    <TaskCompletionForm
      actionsComponent={actionsComponent}
      defaultValues={defaultValues}
      error={taskFormTemplateError}
      fields={formFields}
      formRef={formRef}
      formTemplateId={formTemplateId}
      hasVisibleFields={hasVisibleFields}
      id={id}
      isViewLocked={isViewLocked}
      loading={taskFormTemplateLoading}
      onMarkedAsRead={onLinkedDocsMarkedAsRead}
      onSubmit={(values) =>
        onTaskCompletionCreateMutation(values, {
          readRecords,
          docsByFieldId,
        })
      }
      taskCompletionError={taskCompletionError}
      taskCompletionLoading={
        taskCompletionLoading || taskCompletionCreateMutationLoading
      }
      taskGroupKey={taskGroupKey}
    />
  );
}

export default TaskCompletionCreateContainer;
